import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { FilterRequestForCombo, ProductsService, CategoriesService, CollectionsService, ProductTypesService, BrandsService } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { FiltersPanelChangeEvent, FiltersPanelDropdownLazyLoadEvent, FiltersPanelFieldOverrides } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { buildRemoteDropdownRequest, normalizeDropdownState } from '@src/app/shared/components/filters-panel/filters-panel-dropdown.helpers';
import { ActiveInactivePipe } from '@app/shared/pipes/activeInactive.pipe';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';

@Component({
    selector: 'app-products',
    templateUrl: './products.component.html',
    styleUrl: './products.component.scss',
    host: {
        class: 'app-products'
    },
    standalone: false
})
export class ProductsComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();

  public readonly productsTableName = TableSettingService.ProductsTableName;

  filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  // Dropdown: Categories
  categories = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  // Dropdown: isActive
  statusOptions: Array<{ id: string | number; text: string }> = [];

  // Dropdown: Collections
  collections = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  // Dropdown: product types
  productTypes = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  // Dropdown: brands
  brands = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  allowCreateProduct: boolean = false;

  constructor(
    private translateService: TranslateService,
    private router: Router,
    public override tableSettingService: TableSettingService,
    private productsService: ProductsService,
    private permissionService: PermissionService,
    private categoriesService: CategoriesService,
    private collectionsService: CollectionsService,
    private productTypesService: ProductTypesService,
    private brandService: BrandsService
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    // i18n propagation on lang change
    this.rebuildStatusOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildStatusOptions();
        this.rebuildFiltersPanelOverrides();
        this.rebuildSortPanelColumnLabels();
      });

    this.allowCreateProduct = await this.permissionService.hasActionPermissions(Actions.create_products);

    this.tableSettingService.initProducts();
    await super.setCache(this.productsTableName);

    this.initSortPanel({
      defaultSort: { key: 'sku', direction: 'ASC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.productsService.apiProductsFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.productsTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.productsTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  private rebuildStatusOptions() {
    const pipe = new ActiveInactivePipe(this.translateService);
    this.statusOptions = pipe.values;
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    // Update sort labels without resetting the current sort selection
    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();

        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        return {
          key: col.field,
          label: translated && translated !== translationKey ? translated : col.header
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  goToProductDetails(event: any, productId: string) {
    this.router.navigate([`/products/${productId}`]);
  }

  goToCreateProductForm() {
    this.router.navigate(['/products/add']);
  }

  private rebuildFiltersPanelOverrides() {
    this.filtersPanelFieldOverrides = {
      'categories.id': {
        fieldLabel: 'TABLE.HEADER.Categories',
        isMultiselect: true,

        options: this.categories.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.categories.totalCount ?? 0,
        totalPages: this.categories.totalPages ?? 1,
        pageIndex: this.categories.pageIndex ?? 1,
        hasPreviousPage: this.categories.hasPreviousPage ?? false,
        hasNextPage: this.categories.hasNextPage ?? false
      },
      'collections.id': {
        fieldLabel: 'TABLE.HEADER.Collections',
        isMultiselect: true,

        options: this.collections.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.collections.totalCount ?? 0,
        totalPages: this.collections.totalPages ?? 1,
        pageIndex: this.collections.pageIndex ?? 1,
        hasPreviousPage: this.collections.hasPreviousPage ?? false,
        hasNextPage: this.collections.hasNextPage ?? false
      },
      'isActive': {
        fieldLabel: 'TABLE.HEADER.Status',
        isMultiselect: true,

        options: this.statusOptions,
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      productTypeId: {
        fieldLabel: 'TABLE.HEADER.ProductType',
        isMultiselect: true,

        options: this.productTypes.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.productTypes.totalCount ?? 0,
        totalPages: this.productTypes.totalPages ?? 1,
        pageIndex: this.productTypes.pageIndex ?? 1,
        hasPreviousPage: this.productTypes.hasPreviousPage ?? false,
        hasNextPage: this.productTypes.hasNextPage ?? false
      },
      brandId: {
        fieldLabel: 'TABLE.HEADER.Brand',
        isMultiselect: true,

        options: this.brands.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.brands.totalCount ?? 0,
        totalPages: this.brands.totalPages ?? 1,
        pageIndex: this.brands.pageIndex ?? 1,
        hasPreviousPage: this.brands.hasPreviousPage ?? false,
        hasNextPage: this.brands.hasNextPage ?? false
      }
    };
  }

  async onFiltersPanelDropdownLazyLoad(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    if (!evt) {
      return;
    }

    // fetch dropdowns remote data
    const handlers: Record<string, (e: FiltersPanelDropdownLazyLoadEvent) => Promise<void>> = {
      'categories.id': (e) => this.loadCategoriesCombo(e),
      'collections.id': (e) => this.loadCollectionsCombo(e),
      productTypeId: (e) => this.loadProductTypesCombo(e),
      brandId: (e) => this.loadBrandsCombo(e)
    };

    const handler = handlers[evt.fieldKey];
    if (!handler) {
      return;
    }

    await handler(evt);
  }

  private async loadCategoriesCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
      pageIndex: evt.pageIndex,
      pageSize: evt.pageSize,
      search: evt.search
    });

    const response = await firstValueFrom(
      this.categoriesService.apiCategoriesFilterComboPost(requestPayload)
    );

    this.categories = normalizeDropdownState(response as any, evt.pageIndex) as any;
    this.rebuildFiltersPanelOverrides();
  }

  private async loadCollectionsCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
      pageIndex: evt.pageIndex,
      pageSize: evt.pageSize,
      search: evt.search
    });

    const response = await firstValueFrom(
      this.collectionsService.apiCollectionsFilterComboPost(requestPayload)
    );

    this.collections = normalizeDropdownState(response as any, evt.pageIndex) as any;
    this.rebuildFiltersPanelOverrides();
  }

  private async loadProductTypesCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
      pageIndex: evt.pageIndex,
      pageSize: evt.pageSize,
      search: evt.search
    });

    const response = await firstValueFrom(
      this.productTypesService.apiProductTypesFilterComboPost(requestPayload)
    );

    this.productTypes = normalizeDropdownState(response as any, evt.pageIndex) as any;
    this.rebuildFiltersPanelOverrides();
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;
    const isApplyAction = evt.source === 'apply';
    const isClearAction = evt.source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  private async loadBrandsCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
      pageIndex: evt.pageIndex,
      pageSize: evt.pageSize,
      search: evt.search
    });

    const response = await firstValueFrom(
      this.brandService.apiBrandsFilterComboPost(requestPayload)
    );

    this.brands = normalizeDropdownState(response as any, evt.pageIndex) as any;
    this.rebuildFiltersPanelOverrides();
  }
}
